import type { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { NOTEBOOK_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { useInvalidateFetchFeed } from '@/features/CustomFeedV2/hooks';
import { QUERY_KEY } from '@/features/ManageTags/common';
import type { Notebook } from '@/features/Notes/types';

import type { UseFeedNotebooksResponse } from './useFeedNotebooks';
import { useRefetchAllNotebooks } from './useFetchNotebooks';

type MutationData = {
  name: string;
  feedId?: string;
  channelId?: string;
  type?: string;
};

export const useCreateNotebook = () => {
  const queryClient = useQueryClient();
  const refetchAllNotebooks = useRefetchAllNotebooks();
  const refetchBoardDefinition = useInvalidateFetchFeed();

  return useMutation(
    ({ name, feedId, channelId, type }: MutationData) =>
      axiosInstance.put<CreateNotebookResponseType>(
        NOTEBOOK_API_ROUTES.index(),
        {
          name,
          ...(type && { feedId, channelId }),
        }
      ),
    {
      onMutate: ({ name, channelId, feedId, type }) => {
        if (!channelId && !feedId) return;

        const prevData = queryClient.getQueryData<
          AxiosResponse<UseFeedNotebooksResponse>
        >([QUERY_KEY, feedId, channelId]);

        if (prevData) {
          const newNotebook: Notebook = {
            notebook_name: name,
            notes: [],
            notebook_type: type || 'user',
            // temp data for optimistic update
            notebook_id: Math.random().toString(36).substr(2, 9),
            notebook_created_at: new Date().toISOString(),
            notebook_updated_at: new Date().toISOString(),
            default: !!(feedId || channelId),
          };

          let updatedNotebooks = [...prevData.data.results];
          if (type) {
            updatedNotebooks = [newNotebook, ...prevData.data.results];
          } else {
            /**
             * Remember that the default notebooks are always at the top of the list
             * So we need to find the index of the default notebook and insert the new notebook along with the rest of the user notebooks
             * If there are no default notebooks, we insert the new notebook and sort the list by name
             */
            let defaultNotebook: Notebook | null = null;

            const defaultNotebookIndex = updatedNotebooks.findIndex(
              ({ notebook_type }) => notebook_type === 'default'
            );

            if (defaultNotebookIndex >= 0) {
              defaultNotebook = updatedNotebooks.splice(
                defaultNotebookIndex,
                1
              )[0];
            }

            updatedNotebooks = [...updatedNotebooks, newNotebook].sort(
              (notebookA, notebookB) =>
                notebookA.notebook_name.localeCompare(notebookB.notebook_name)
            );

            if (defaultNotebookIndex >= 0 && defaultNotebook) {
              // insert at the same index
              updatedNotebooks.splice(defaultNotebookIndex, 0, defaultNotebook);
            }
          }

          const updatedData = {
            ...prevData,
            data: {
              ...prevData.data,
              results: updatedNotebooks,
            },
          };

          queryClient.setQueryData<
            AxiosResponse<UseFeedNotebooksResponse> | undefined
          >([QUERY_KEY, feedId, channelId], updatedData);
        }

        return { prevData };
      },
      onError: (_, { channelId, feedId }, context: Context) => {
        if (!(channelId || feedId) || !context || !context.prevData) return;

        queryClient.setQueryData<AxiosResponse<UseFeedNotebooksResponse>>(
          [QUERY_KEY, feedId, channelId],
          context.prevData
        );
      },
      onSuccess: async (_, { channelId, feedId }) => {
        const promises = [refetchAllNotebooks()];

        if (channelId || feedId) {
          promises.push(
            queryClient.invalidateQueries([QUERY_KEY, feedId, channelId])
          );
        }

        if (feedId) {
          promises.push(refetchBoardDefinition(feedId));
        }

        await Promise.all(promises);
      },
    }
  );
};

type Context =
  | { prevData?: AxiosResponse<UseFeedNotebooksResponse> }
  | undefined;

export type CreateNotebookResponseType = {
  results: {
    notebookId: string;
  };
};
